import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable, SubHeading, Switch } from "../../shared/Components";
import {
  deleteAxios,
  getAxios,
  patchAxios,
} from "../../shared/Services/commonService";
import {
  API_DELETE_FORM,
  API_GET_FORM,
  API_UPDATE_FORM_STATUS,
} from "../../utils/api/ApiConstant";
import Icon from "../../assets/icons/Icon";
import { dispatch } from "../../store";
import { uiActions } from "../../store/ui/ui-slice";
import { userAction } from "../../store/user/user-slice";

const ConfigForm = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAxios(API_GET_FORM);
      console.log("🚀 ~ ConfigForm ~ response:", response);
      setData(response || []);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formattedList = data.map((parent) => ({
    label: parent?.name,
    value: parent?.id,
  }));
  dispatch(userAction.setParentIdList(formattedList));

  const parentIdList = useMemo(() => {
    const mapping = new Map();

    // First pass: create all nodes
    data?.forEach((el) => {
      mapping.set(el.id, { label: el.name, value: el.id, children: [] });
    });

    // Second pass: build relationships
    const rootNodes = [];
    data?.forEach((el) => {
      const node = mapping.get(el.id);
      if (el.parentId === null) {
        rootNodes.push(node);
      } else {
        const parent = mapping.get(el.parentId);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    return rootNodes;
  }, [data]);

  const handleOpenModal = useCallback(
    (modalName, modalTitle, rowData = {}) => {
      dispatch(
        uiActions.openModal({
          name: modalName,
          modalTitle,
          rowData: { ...rowData, parentIdList },
          size: "md",
          fetchData,
        }),
      );
    },
    [dispatch, fetchData, parentIdList],
  );

  const handleSwitch = async (row) => {
    try {
      const updatedStatus = !row.isActive;

      const response = await patchAxios(`${API_UPDATE_FORM_STATUS}/${row.id}`, {
        isActive: updatedStatus,
        rowVersion: row.rowVersion,
      });
      if (response?.success) {
        setData((prevData) =>
          prevData.map((item) =>
            item.id === row.id
              ? {
                  ...item,
                  isActive: response.data.isActive,
                  rowVersion: response.data.rowVersion,
                }
              : item,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to update permission:", error);
    }
  };
  const handleDelete = async (row) => {
    try {
      const response = await deleteAxios(`${API_DELETE_FORM}/${row.id}`);

      setData((prevData) => {
        const index = prevData.findIndex((item) => item.id === row.id);
        return index !== -1 ? prevData.toSpliced(index, 1) : prevData;
      });
    } catch (error) {
      console.error("Failed to delete permission:", error);
    }
  };

  const handleDeleteConfirm = (rowData) => {
    dispatch(
      uiActions.openModal({
        name: "CONFIRMATION",
        showHeader: false,
        size: "sm",
        rowData: {
          modalUsesFor: {
            use: "delete",
            confirmationMessage: `Are you sure you want to delete ${rowData?.name}?`,
            onConfirm: () => handleDelete(rowData),
          },
        },
      }),
    );
  };
  const handleConfirm = (rowData) => {
    dispatch(
      uiActions.openModal({
        name: "CONFIRMATION",
        showHeader: false,
        size: "sm",
        rowData: {
          modalUsesFor: {
            use: "status",
            confirmationMessage: `Are you sure you want to ${
              rowData?.isActive ? "deactivate" : "activate"
            } ${rowData?.name}?`,
            onConfirm: () => handleSwitch(rowData),
          },
        },
      }),
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "key",
        header: "Key",
      },
      {
        accessorKey: "path",
        header: "Path",
      },
      {
        accessorKey: "description",
        header: "Description",
      },
      {
        accessorKey: "isActive",
        header: "isActive",
        cell: ({ getValue, row }) => {
          return (
            <Switch
              getValue={getValue}
              onChange={() => handleConfirm(row.original)}
            />
          );
        },
      },
      {
        accessorKey: "actionOn",
        header: "ActionOn",
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => {
          return (
            <>
              <Icon
                name="FaEdit"
                className="icon_button"
                cursorPointer={true}
                onClick={() =>
                  handleOpenModal("FORMMODAL", "Edit ", row.original)
                }
              />
              <Icon
                name="FaTrash"
                className=" icon_button"
                cursorPointer={true}
                onClick={() => handleDeleteConfirm(row.original)}
              />
            </>
          );
        },
      },
    ],
    [handleOpenModal, handleConfirm],
  );

  return (
    <>
      <SubHeading
        subTitle="Form"
        isAdd={true}
        handleShowHide={() => handleOpenModal("FORMMODAL", "Create ")}
      />
      <div className="card-body">
        <DataTable
          columns={columns}
          data={data}
          isSearchable={false}
          isPagination={false}
          isPageSize={false}
          isLoading={isLoading}
        />
      </div>
    </>
  );
};

export default ConfigForm;
